import './ads-section-styte.scss'

import { useUserRegionCH } from '@cdc-internal/mkt-nav-footer'
import { motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import useWindowSize from '@/hooks/useWindowSize'
import Box from '@/modules/components/Box'
import Button from '@/modules/components/Button'
import { useIsMobile } from '@/modules/components/hooks/useIsMobile'
import { useOneLink } from '@/modules/components/hooks/useOneLink'
import { useUserAgent } from '@/modules/components/hooks/useUserAgent'
import Input from '@/modules/components/Input'
import Link from '@/modules/components/Link'
import Typography from '@/modules/components/Typography'
import { validateEmail } from '@/utils/validate'

import appStoreBadge from './apple-playstore-outline.webp'
import { GeetestComponent } from './GeetestComponent'
import playStoreBadge from './google-playstore-outline.webp'
import PhoneFrame from './phone-screen.webp'
import PhoneFrameMobile from './phone-screen-mb.webp'
import ScanToDownloadSrc from './scan-to-download.webp'
import { submitEmail } from './utils/axios'

const transitionParams = { duration: 0.1, bounce: 0, ease: 'linear' }
const transitionParamsScanToDownload = {
  duration: 1,
  bounce: 0,
  ease: 'linear',
}

const SubmitButton = styled(Button)`
  position: absolute;
  width: 137px;
  height: 40px;
  top: 4px;
  right: 6px;
  padding: 14px 24px;
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  line-height: 100%;
  letter-spacing: 0.015em;
  text-align: center;
  background-color: #061121;
  border: 0.5px solid #061121;
  border-radius: 37px;
`

const DarkCover = styled(motion.div)`
  position: absolute;
  width: 90%;
  height: 95%;
  background: rgba(6, 17, 33, 0.5);
  border-radius: 28px;
  backdrop-filter: blur(1.5px);
  top: 13px;
  left: 12px;
`

const AdsSection = () => {
  const { t, i18n } = useTranslation()
  const { queryString } = useOneLink()
  const isMobile = useIsMobile()
  const [emailAddress, setEmailAddress] = useState('')
  const [isValid, setIsValid] = useState(true)
  const [isSent, setIsSent] = useState(false)
  const userAgent = useUserAgent()
  const isIos = userAgent === 'ios'
  const geetestRef = useRef(undefined)
  const userRegion = useUserRegionCH()

  const isSSR = typeof window === 'undefined'
  const windowSize = useWindowSize()

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isSent) {
        setIsSent(false)
        setEmailAddress('')
      }
    }, 8000)
    return () => clearTimeout(timer)
  }, [isSent])

  const handleInputEmail = (e) => {
    const { value } = e.target
    setEmailAddress(value)
    setIsValid(true)
  }

  const handleEmail = async (captchaValidateResult) => {
    try {
      await submitEmail({
        email: emailAddress,
        subscription: true,
        challenge: captchaValidateResult.geetest_challenge,
        seccode: captchaValidateResult.geetest_seccode,
      })
      setIsSent(true)
    } catch (err) {
      geetestRef.current?.reset()
      setIsValid(false)
    }
  }

  return (
    <section>
      <Box
        display="flex"
        width={'100%'}
        mb={[0, '48px']}
        flexDirection={['column', 'row']}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Box
          display="flex"
          width={['100%', '377px', '50%']}
          flexDirection="column"
          alignItems={['center', 'flex-start']}
          justifyContent={'center'}
          customCss={{
            gap: '24px',
          }}
        >
          <Typography
            variant="plain"
            textAlign={['center', 'left']}
            fontSize={[32, 30, 36]}
            fontWeight={[700]}
            lineHeight={['125%']}
            color="#0B1426"
            maxWidth={[328, 377, 491]}
          >
            {t('ads.title')}
          </Typography>
          <Typography
            variant="plain"
            textAlign={['center', 'left']}
            fontSize={[16, 20]}
            fontWeight={[400]}
            lineHeight={['170%', '150%']}
            color="#0B1426"
            maxWidth={[328, 377, 476]}
          >
            {t('ads.subtitle')}
          </Typography>
          {userRegion !== 'KR' && (
            <>
              <Box width={[327, 381, 459]} position="relative">
                <Input
                  type="email"
                  variant="email"
                  placeholder={t('ads.register.placeholder')}
                  width={'100%'}
                  onChange={handleInputEmail}
                  value={emailAddress}
                  flex={1}
                />
                <SubmitButton
                  opacity={emailAddress.length ? 1 : 0.6}
                  onClick={() => {
                    setIsValid(validateEmail(emailAddress))
                    if (validateEmail(emailAddress)) {
                      console.log('varified')
                      geetestRef.current?.verify()
                    }
                  }}
                >
                  {t('ads.register.cta_button')}
                </SubmitButton>
                <Box mt={2} mb={'-8px'} maxWidth={['327px', '519px']}>
                  {!isValid && (
                    <Typography variant="adSectionSubscribeMsg" color="#D9475A">
                      {t('ads.subscription.error')}
                    </Typography>
                  )}
                  {isSent && (
                    <Typography variant="adSectionSubscribeMsg" color="#00A68C">
                      {t('ads.subscription.success')}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <GeetestComponent
                    ref={geetestRef}
                    onVerifySuccess={(data) => handleEmail(data)}
                    geetestConfig={{
                      product: 'bind',
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="plain"
                as="span"
                maxWidth={['327px', '381px', '504px']}
                fontWeight={500}
                fontSize={'12px'}
                lineHeight={'145%'}
                letterSpacing={'0.025em'}
                color={'#565F76'}
              >
                <Trans
                  i18nKey="ads.privacy"
                  components={{
                    a: (
                      <a
                        target="_blank"
                        href={
                          i18n.language === 'ko'
                            ? 'https://crypto.com/privacy/southkorea'
                            : 'https://crypto.com/privacy/global'
                        }
                        style={{ textDecoration: 'underline' }}
                        rel="noreferrer"
                      />
                    ),
                  }}
                ></Trans>
              </Typography>
              <Box>
                {isMobile && isIos && (
                  <Link href={`https://crypto.onelink.me/RSfq${queryString}`}>
                    <Box
                      as="img"
                      src={appStoreBadge}
                      alt="download crypto app"
                      width={130}
                      height={42}
                    />
                  </Link>
                )}
                {isMobile && !isIos && (
                  <Link href={`https://crypto.onelink.me/veNW${queryString}`}>
                    <Box
                      as="img"
                      src={playStoreBadge}
                      alt="download crypto app"
                      width={130}
                      height={42}
                    />
                  </Link>
                )}
              </Box>
            </>
          )}
        </Box>
        {!isSSR && (
          <Box
            display={['none', 'block', 'block']}
            position="relative"
            overflow={['hidden']}
            width={[210]}
            height={[430]}
            ml={['0px', '73px', '100px']}
            mt={['63px', 0, 0]}
            borderRadius={['38px']}
            customCss={{
              filter:
                'drop-shadow(-24px 24px 64px rgba(0, 0, 0, 0.15)) drop-shadow(-8px 8px 32px rgba(0, 0, 0, 0.20))',
            }}
          >
            <Box
              position={'absolute'}
              as="img"
              src={PhoneFrame}
              width={'100%'}
              height={'100%'}
              alt="Mobile phone screen displaying total balance with Crypto.com App"
            />
            <DarkCover
              viewport={{ once: true }}
              transition={{ ...transitionParams, delay: 1.5 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
            ></DarkCover>
            <motion.img
              initial={{ bottom: '-263px', opacity: 0 }}
              animate={{ bottom: '9px', opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                ...transitionParamsScanToDownload,
                delay: 1.8,
              }}
              style={{ width: '90%', left: '11px', position: 'absolute' }}
              src={ScanToDownloadSrc}
              alt="Mobile phone screen displaying available tokens for Pay Checkout: CRO, BTC, ETH, USDT, XRP, and USDC. Option to send payment now."
            />
          </Box>
        )}
        <Box display={['block', 'none', 'none']} width={257} height={344}>
          <img
            height={'100%'}
            width={'100%'}
            src={PhoneFrameMobile}
            alt="Mobile phone screen displaying total balance with Crypto.com App"
            loading="lazy"
          />
        </Box>
      </Box>
    </section>
  )
}

export default AdsSection
